/**
 * Memory monitoring utility for tracking browser memory usage
 * Only works in Chrome and Chromium-based browsers
 */

interface MemoryInfo {
  timestamp: number;
  jsHeapSizeLimit: number;
  totalJSHeapSize: number;
  usedJSHeapSize: number;
  usagePercentage: number;
}

interface MemoryMonitorOptions {
  interval?: number; // Monitoring interval in milliseconds
  maxSamples?: number; // Maximum number of samples to keep
  warningThreshold?: number; // Warning threshold percentage (0-100)
  criticalThreshold?: number; // Critical threshold percentage (0-100)
  onWarning?: (info: MemoryInfo) => void; // Callback when warning threshold is reached
  onCritical?: (info: MemoryInfo) => void; // Callback when critical threshold is reached
  onSample?: (info: MemoryInfo) => void; // Callback on each sample
  enabled?: boolean; // Whether monitoring is enabled
}

class MemoryMonitor {
  private options: MemoryMonitorOptions;
  private samples: MemoryInfo[] = [];
  private intervalId: number | null = null;
  private isSupported: boolean = false;

  constructor(options: MemoryMonitorOptions = {}) {
    this.options = {
      interval: 10000, // Default: check every 10 seconds
      maxSamples: 100, // Default: keep 100 samples
      warningThreshold: 70, // Default: warn at 70% usage
      criticalThreshold: 90, // Default: critical at 90% usage
      enabled: true, // Default: enabled in all environments
      ...options,
    };

    // Check if memory API is supported (Chrome/Chromium only)
    this.isSupported =
      typeof performance !== 'undefined' &&
      (performance as any).memory !== undefined;

    if (!this.isSupported) {
      console.warn(
        'Memory monitoring is not supported in this browser. Only Chrome/Chromium browsers support the performance.memory API.',
      );
    }
  }

  /**
   * Start monitoring memory usage
   */
  start(): void {
    if (!this.isSupported || !this.options.enabled) return;

    if (this.intervalId !== null) {
      this.stop(); // Stop existing monitoring
    }

    // Take initial sample
    this.takeSample();

    // Set up interval for regular sampling
    this.intervalId = window.setInterval(() => {
      this.takeSample();
    }, this.options.interval);

    console.log('Memory monitoring started');
  }

  /**
   * Stop monitoring memory usage
   */
  stop(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('Memory monitoring stopped');
    }
  }

  /**
   * Take a memory usage sample
   */
  takeSample(): MemoryInfo | null {
    if (!this.isSupported || !this.options.enabled) return null;

    try {
      const memory = (performance as any).memory;

      const sample: MemoryInfo = {
        timestamp: Date.now(),
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
        totalJSHeapSize: memory.totalJSHeapSize,
        usedJSHeapSize: memory.usedJSHeapSize,
        usagePercentage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
      };

      // Add to samples array
      this.samples.push(sample);

      // Trim samples if exceeding maxSamples
      if (this.samples.length > (this.options.maxSamples || 100)) {
        this.samples.shift();
      }

      // Check thresholds and trigger callbacks
      if (sample.usagePercentage >= (this.options.criticalThreshold || 90)) {
        // In production, only log to console if explicitly enabled
        if (process.env.NODE_ENV === 'development' || this.options.enabled) {
          console.error(
            `CRITICAL: Memory usage at ${sample.usagePercentage.toFixed(2)}%`,
          );
        }
        this.options.onCritical?.(sample);
      } else if (
        sample.usagePercentage >= (this.options.warningThreshold || 70)
      ) {
        // In production, only log to console if explicitly enabled
        if (process.env.NODE_ENV === 'development' || this.options.enabled) {
          console.warn(
            `WARNING: Memory usage at ${sample.usagePercentage.toFixed(2)}%`,
          );
        }
        this.options.onWarning?.(sample);
      }

      // Call onSample callback if provided
      this.options.onSample?.(sample);

      return sample;
    } catch (error) {
      console.error('Error taking memory sample:', error);
      return null;
    }
  }

  /**
   * Get all collected samples
   */
  getSamples(): MemoryInfo[] {
    return [...this.samples];
  }

  /**
   * Get the latest sample
   */
  getLatestSample(): MemoryInfo | null {
    return this.samples.length > 0
      ? this.samples[this.samples.length - 1]
      : null;
  }

  /**
   * Clear all samples
   */
  clearSamples(): void {
    this.samples = [];
  }

  /**
   * Get memory usage statistics
   */
  getStats() {
    if (this.samples.length === 0) return null;

    const usageValues = this.samples.map((sample) => sample.usagePercentage);

    return {
      min: Math.min(...usageValues),
      max: Math.max(...usageValues),
      avg: usageValues.reduce((sum, val) => sum + val, 0) / usageValues.length,
      current: this.getLatestSample()?.usagePercentage || 0,
      sampleCount: this.samples.length,
    };
  }

  /**
   * Export memory data as JSON
   */
  exportData(): string {
    return JSON.stringify({
      samples: this.samples,
      stats: this.getStats(),
      timestamp: Date.now(),
    });
  }

  /**
   * Check if memory monitoring is supported in this browser
   */
  isBrowserSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Check if memory monitoring is currently active
   */
  isActive(): boolean {
    return this.intervalId !== null;
  }
}

// Create a singleton instance
const memoryMonitor = new MemoryMonitor();

export default memoryMonitor;
export { MemoryMonitor, type MemoryInfo, type MemoryMonitorOptions };
